import { TranslateModule } from '@ngx-translate/core';
import { AppUpdateService } from '@services/app-update/app-update.service';
import { ButtonModule } from 'primeng/button';
import { animate, style, transition, trigger } from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, viewChild } from '@angular/core';

/** CSS változó a <html> elemen: ennyivel kell az oldal alját a sáv fölé emelni. */
export const APP_UPDATE_OFFSET_VARIABLE = '--app-update-offset';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const BANNER_ENTER = trigger('bannerEnter', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateY(1rem)' }),
    animate('250ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
  ])
]);

/**
 * „Új verzió érhető el” sáv. Mobilon alul teljes szélességben, asztalon (lg, 992px) lebegő kártya alul középen.
 * Amíg látszik, a --app-update-offset változóval az oldal alját fölé emeli.
 * Spec: docs/superpowers/specs/2026-09-16-app-update-detection-design.md (8)
 */
@Component({
  selector: 'app-update-banner',
  standalone: true,
  imports: [TranslateModule, ButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [BANNER_ENTER],
  template: `
    @if (updates.updateAvailable()) {
      <div
        #dock
        class="pointer-events-none fixed inset-x-0 bottom-0 z-[1050] flex justify-center lg:bottom-6 lg:px-4"
      >
        <div
          data-app-update-banner
          role="status"
          aria-live="polite"
          [@bannerEnter]
          [@.disabled]="reducedMotion"
          class="pointer-events-auto flex w-full items-center gap-3 border-t border-surface-700 bg-surface-900 px-4 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))] pt-3 text-surface-0 shadow-[0_-4px_16px_rgba(0,0,0,0.18)] lg:max-w-[28rem] lg:rounded-xl lg:border lg:px-5 lg:py-4 lg:shadow-2xl dark:border-surface-200 dark:bg-surface-0 dark:text-surface-900"
        >
          <i
            class="pi pi-sparkles shrink-0 text-lg text-primary-300 dark:text-primary-600"
            aria-hidden="true"
          ></i>
          <div class="min-w-0 flex-1">
            <p class="m-0 font-semibold leading-snug">{{ 'appUpdate.available' | translate }}</p>
            <p class="m-0 mt-0.5 hidden text-sm leading-snug text-surface-300 lg:block dark:text-surface-600">
              {{ 'appUpdate.hint' | translate }}
            </p>
          </div>
          <p-button
            [label]="'appUpdate.reload' | translate"
            icon="pi pi-refresh"
            [loading]="isReloading()"
            [disabled]="isReloading()"
            styleClass="min-h-[44px] whitespace-nowrap"
            (onClick)="updates.reloadNow()"
          />
        </div>
      </div>
    }
  `
})
export class AppUpdateBannerComponent {
  protected readonly updates = inject(AppUpdateService);
  protected readonly isReloading = computed(() => this.updates.status() === 'reloading');
  private readonly document = inject(DOCUMENT);
  protected readonly reducedMotion = !!this.document.defaultView?.matchMedia?.(REDUCED_MOTION_QUERY).matches;
  private readonly dock = viewChild<ElementRef<HTMLElement>>('dock');

  constructor() {
    effect((onCleanup) => {
      const dock = this.dock()?.nativeElement;
      if (dock) {
        onCleanup(this.trackOffset(dock));
      }
    });
  }

  /** A sáv magassága + alsó térköze (asztalon 1.5rem) a változóba; a transform-animáció nem befolyásolja. */
  private trackOffset(dock: HTMLElement): () => void {
    const root = this.document.documentElement;
    const update = (): void => {
      const bottomGap = parseFloat(getComputedStyle(dock).bottom) || 0;
      root.style.setProperty(APP_UPDATE_OFFSET_VARIABLE, `${dock.offsetHeight + bottomGap}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(dock);
    observer.observe(root);
    return () => {
      observer.disconnect();
      root.style.removeProperty(APP_UPDATE_OFFSET_VARIABLE);
    };
  }
}
